import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { requestProUpgrade, resetProUpgrade } from '../actions/license';

/**
 * PlanesView (/planes)
 *
 * Full-page licence comparison: BÁSICA vs PRO, side by side.
 * Replaces the old sidebar pop-up with a dedicated navigable page.
 * Customers on BÁSICA see the "Solicitar actualización a PRO" button.
 * Customers already on PRO see a confirmation banner.
 */

const ACCENT = '#f59e0b';
const DARK   = '#001d3a';

/* ── Feature comparison data ── */
const BASIC_FEATURES = [
  { icon: '📊', label: 'Ventas', desc: 'Reporte completo de ventas por máquina y fecha.' },
  { icon: '🖥️', label: 'Kioscos', desc: 'Gestión y monitorización de tus máquinas.' },
  { icon: '💬', label: 'Soporte', desc: 'Acceso al portal de tickets y FAQs.' },
  { icon: '📖', label: 'Manuales', desc: 'Documentación de uso de la plataforma.' },
];

const PRO_FEATURES = [
  { icon: '⭐', label: 'Todo lo de BÁSICA', desc: 'Incluye todas las funcionalidades de la licencia Básica.', highlight: true },
  { icon: '📷', label: 'Códigos QR', desc: 'Genera y gestiona QRs para apertura de lockers.' },
  { icon: '📱', label: 'Terminales Operativo', desc: 'Gestión de terminales vinculados.' },
  { icon: '💳', label: 'Venta de lockers', desc: 'Punto de venta integrado para tu equipo.' },
  { icon: '🏷️', label: 'Códigos de descuento', desc: 'Crea y distribuye códigos promocionales.' },
  { icon: '🎟️', label: 'Códigos de canje', desc: 'Sistema de vales y bonos.' },
  { icon: '🗓️', label: 'Reservas y calendarios', desc: 'Disponibilidad avanzada y pre-asignaciones.' },
  { icon: '✉️', label: 'Plantillas de email', desc: 'Personaliza los correos a tus clientes.' },
  { icon: '🤝', label: 'Partners', desc: 'Gestión de acuerdos comerciales.' },
  { icon: '📈', label: 'Dashboard KPI', desc: 'Indicadores clave en tiempo real.' },
  { icon: '🔄', label: 'Dashboard operacional', desc: 'Vista global de todas tus máquinas.' },
  { icon: '📉', label: 'Localización avanzada', desc: 'Análisis de ocupación por zona.' },
  { icon: '🔐', label: 'Recuperación de PINes', desc: 'Historial completo de recuperaciones.' },
  { icon: '🗄️', label: 'Auditoría', desc: 'Trazabilidad de todas las acciones.' },
  { icon: '💰', label: 'Retiros', desc: 'Gestión de retiros de efectivo.' },
  { icon: '🔌', label: 'API de exportación', desc: 'Conecta tu ERP/SAP automáticamente.' },
  { icon: '💬', label: 'Chat de soporte técnico', desc: 'Canal directo con el equipo técnico.' },
  { icon: '👥', label: 'Gestión de usuarios', desc: 'Crea usuarios Operativo y Cash-Control.' },
];

class PlanesView extends Component {
  componentDidMount() {
    this.props.resetProUpgrade();
  }

  render() {
    const { type, proUpgradeLoading, proUpgradeSent, proUpgradeError, requestProUpgrade } = this.props;
    const isPro   = type === 'LICENCIA_PRO';
    const isBasic = type === 'LICENCIA_BASICA';

    return (
      <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: '#f4f6f9', minHeight: '100vh', paddingBottom: 60 }}>

        {/* ── Hero ── */}
        <div style={{ background: `linear-gradient(135deg, ${DARK} 0%, #003366 100%)`, padding: '40px 48px 36px', borderBottom: `4px solid ${ACCENT}` }}>
          <h1 style={{ fontSize: 30, fontWeight: 700, color: '#fff', margin: '0 0 10px' }}>
            Planes de licencia
          </h1>
          <p style={{ color: '#a8c8e8', fontSize: 15, margin: 0, maxWidth: 580, lineHeight: 1.7 }}>
            Compara lo que incluye cada plan y elige el que mejor se adapta a tu negocio.
          </p>
        </div>

        <div style={{ padding: '36px 0' }}>

          {/* ── Current plan banner ── */}
          {isPro && (
            <div style={{ background: 'linear-gradient(135deg, #065f46, #047857)', borderRadius: 10, padding: '16px 24px', marginBottom: 28, marginLeft: 24, marginRight: 24, color: '#fff', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 24 }}>✅</span>
              <div>
                <strong style={{ fontSize: 15 }}>Ya tienes la licencia PRO</strong>
                <p style={{ margin: 0, fontSize: 13, color: '#a7f3d0', marginTop: 2 }}>
                  Tienes acceso completo a todas las funcionalidades de la plataforma.
                </p>
              </div>
            </div>
          )}

          {/* ── Comparison grid ── */}
          <div className="planes-grid">

            {/* BÁSICA card */}
            <div style={{ background: '#fff', borderRadius: 12, border: '2px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>📦</div>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: DARK, margin: '0 0 4px' }}>BÁSICA</h2>
                <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Acceso esencial para empezar</p>
                {isBasic && (
                  <span style={{ display: 'inline-block', marginTop: 10, background: '#e0f2fe', color: '#0369a1', fontWeight: 700, fontSize: 11, padding: '3px 12px', borderRadius: 20 }}>
                    Tu plan actual
                  </span>
                )}
              </div>
              <div style={{ padding: '14px 16px' }}>
                {BASIC_FEATURES.map(f => (
                  <CompactFeatureRow key={f.label} icon={f.icon} label={f.label} desc={f.desc} />
                ))}
              </div>
            </div>

            {/* PRO card */}
            <div style={{ background: '#fff', borderRadius: 12, border: `2px solid ${ACCENT}`, overflow: 'hidden', boxShadow: `0 4px 24px rgba(245,158,11,0.18)` }}>
              <div style={{ padding: '20px', borderBottom: `1px solid #fde68a`, textAlign: 'center', background: 'linear-gradient(135deg, #fffbeb, #fef3c7)' }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>⭐</div>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: DARK, margin: '0 0 4px' }}>PRO</h2>
                <p style={{ fontSize: 12, color: '#92400e', margin: 0 }}>Acceso completo a todas las funciones</p>
                {isPro && (
                  <span style={{ display: 'inline-block', marginTop: 10, background: '#d1fae5', color: '#065f46', fontWeight: 700, fontSize: 11, padding: '3px 12px', borderRadius: 20 }}>
                    Tu plan actual
                  </span>
                )}
              </div>
              <div style={{ padding: '14px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '2px 10px' }}>
                {PRO_FEATURES.map(f => (
                  <ProFeatureChip key={f.label} icon={f.icon} label={f.label} desc={f.desc} highlight={f.highlight} />
                ))}
              </div>
              {/* CTA — only for BÁSICA users */}
              {isBasic && (
                <div style={{ padding: '0 24px 28px' }}>
                  <hr style={{ border: 'none', borderTop: '1px solid #fde68a', margin: '4px 0 20px' }} />
                  {proUpgradeSent ? (
                    <div style={{ textAlign: 'center', padding: '16px 0' }}>
                      <div style={{ fontSize: 36, marginBottom: 10 }}>✅</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#065f46', marginBottom: 6 }}>¡Solicitud enviada!</div>
                      <p style={{ fontSize: 13, color: '#6b7280', margin: 0, lineHeight: 1.6 }}>
                        Nuestro equipo se pondrá en contacto contigo en breve para gestionar la actualización.
                      </p>
                    </div>
                  ) : (
                    <>
                      {proUpgradeError && (
                        <p style={{ color: '#ef4444', fontSize: 12, marginBottom: 10, textAlign: 'center' }}>
                          No se pudo enviar. Inténtalo de nuevo más tarde.
                        </p>
                      )}
                      <button
                        onClick={requestProUpgrade}
                        disabled={proUpgradeLoading}
                        style={{
                          width: '100%', padding: '14px', borderRadius: 40, border: 'none',
                          background: proUpgradeLoading ? '#d97706' : ACCENT,
                          color: '#fff', fontWeight: 800, fontSize: 15,
                          cursor: proUpgradeLoading ? 'not-allowed' : 'pointer',
                          opacity: proUpgradeLoading ? 0.8 : 1,
                          transition: 'background 0.2s',
                        }}
                      >
                        {proUpgradeLoading ? 'Enviando solicitud…' : '⭐ Solicitar actualización a PRO'}
                      </button>
                      <p style={{ textAlign: 'center', fontSize: 12, color: '#9ca3af', marginTop: 10, marginBottom: 0 }}>
                        Nuestro equipo te contactará en menos de 24 h
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    );
  }
}

const FeatureRow = ({ icon, label, desc, highlight }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 0', borderBottom: '1px solid #f3f4f6' }}>
    <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{icon}</span>
    <div>
      <span style={{ fontSize: 13, fontWeight: highlight ? 700 : 600, color: highlight ? ACCENT : DARK }}>{label}</span>
      <p style={{ margin: 0, fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>{desc}</p>
    </div>
  </div>
);

const CompactFeatureRow = ({ icon, label, desc }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #f3f4f6' }}>
    <span style={{ fontSize: 15, flexShrink: 0 }}>{icon}</span>
    <div>
      <span style={{ fontSize: 13, fontWeight: 600, color: DARK }}>{label}</span>
      <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', lineHeight: 1.4 }}>{desc}</p>
    </div>
  </div>
);

const ProFeatureChip = ({ icon, label, desc, highlight }) => (
  <div style={{
    display: 'flex', alignItems: 'flex-start', gap: 7, padding: '7px 8px',
    borderRadius: 7,
    background: highlight ? '#fffbeb' : 'transparent',
    border: highlight ? `1px solid #fde68a` : '1px solid transparent',
    marginBottom: 2,
  }}>
    <span style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>{icon}</span>
    <div style={{ minWidth: 0 }}>
      <span style={{ fontSize: 12, fontWeight: highlight ? 700 : 600, color: highlight ? ACCENT : DARK, display: 'block', lineHeight: 1.3 }}>{label}</span>
      <span style={{ fontSize: 10, color: '#9ca3af', lineHeight: 1.3, display: 'block' }}>{desc}</span>
    </div>
  </div>
);

const mapStateToProps = (state) => ({
  type: state.auth.type,
  role: state.auth.role,
  proUpgradeLoading: state.license.loading,
  proUpgradeSent:    state.license.sent,
  proUpgradeError:   state.license.error,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  requestProUpgrade,
  resetProUpgrade,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(PlanesView);
